import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEnvelope, FaLock, FaUser, FaSearch, FaCalendarAlt, FaCamera, FaPen, FaEye, FaEyeSlash } from 'react-icons/fa';
import styles from './signup.module.css';
import { API_URL } from '../Utils/api';
import logo from '../images/logo.jpeg'
import { GoogleLogin } from '@react-oauth/google';


const LOGO_BASE = '/logos';

const Logos = Object.fromEntries(
  ['ul','uk','entpe','ensi','infa','enam',
   'ucao','ub','udd','esag','hec','isgis','isma','iut','other']
  .map(id => [
    id,
    <img
      src={`${LOGO_BASE}/${id}.png`}
      alt={id.toUpperCase()}
      className="campusLogoImg"
      onError={e => {
        // Fallback SVG si l'image ne charge pas
        e.currentTarget.style.display = 'none';
        e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex');
      }}
    />
  ])
);

// Composant logo avec fallback si l'image est manquante
const LogoWithFallback = ({ id, short }) => (
  <div style={{width:'100%',height:'100%',position:'relative',display:'flex',alignItems:'center',justifyContent:'center'}}>
    <img
      src={`${LOGO_BASE}/${id}.png`}
      alt={short}
      style={{width:'100%',height:'100%',objectFit:'contain',borderRadius:6}}
      onError={e => {
        e.currentTarget.style.display = 'none';
        e.currentTarget.parentElement.querySelector('.fallback').style.display = 'flex';
      }}
    />
    <div className="fallback" style={{
      display:'none', position:'absolute', inset:0,
      alignItems:'center', justifyContent:'center',
      background:'linear-gradient(135deg,#2563eb,#1d4ed8)',
      borderRadius:6, color:'#fff', fontWeight:800, fontSize:11,
    }}>
      {short}
    </div>
  </div>
);

/* ── Universités ── */
const CAMPUS_LIST = [
  { id:'ul',    name:'Université de Lomé',                      short:'UL',    type:'public',  city:'Lomé' },
  { id:'uk',    name:'Université de Kara',                      short:'UK',    type:'public',  city:'Kara' },
  { id:'entpe', name:'ENTPE',                                    short:'ENTPE', type:'public',  city:'Lomé' },
  { id:'ensi',  name:'ENSI',                                     short:'ENSI',  type:'public',  city:'Lomé' },
  { id:'infa',  name:'INFA',                                     short:'INFA',  type:'public',  city:'Tové' },
  { id:'enam',  name:'ENAM',                                     short:'ENAM',  type:'public',  city:'Lomé' },
  { id:'ucao',  name:'UCAO-UUT',                                 short:'UCAO',  type:'private', city:'Lomé' },
  { id:'ub',    name:'Université Bénin',                         short:'UB',    type:'private', city:'Lomé' },
  { id:'udd',   name:'Université de Droit et Développement',    short:'UDD',   type:'private', city:'Lomé' },
  { id:'esag',  name:'ESAG-NDE',                                 short:'ESAG',  type:'private', city:'Lomé' },
  { id:'hec',   name:'HEC Togo',                                 short:'HEC',   type:'private', city:'Lomé' },
  { id:'isgis', name:'ISGIS',                                    short:'ISGIS', type:'private', city:'Lomé' },
  { id:'isma',  name:'ISMA',                                     short:'ISMA',  type:'private', city:'Lomé' },
  { id:'iut',   name:'IUT de Lomé',                              short:'IUT',   type:'private', city:'Lomé' },
  { id:'other', name:'Autre établissement',                      short:'?',     type:'other',   city:'' },
];

const TOTAL_STEPS = 6;

const Signup = () => {
  const navigate = useNavigate();

  const [step,         setStep]         = useState(1);
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [username,     setUsername]     = useState('');
  const [campus,       setCampus]       = useState('');
  const [campusSearch, setCampusSearch] = useState('');
  const [otp,          setOtp]          = useState(['', '', '', '', '', '']);
  const [otpSent,      setOtpSent]      = useState(false);
  const [otpVerified,  setOtpVerified]  = useState(false);
  const [otpLoading,   setOtpLoading]   = useState(false);
  const [otpCooldown,  setOtpCooldown]  = useState(0);
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [birthday,     setBirthday]     = useState('');
  const [isLoading,    setIsLoading]    = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors,       setErrors]       = useState({});
  const [acceptCgu,    setAcceptCgu]    = useState(false);

  const fileInputRef = useRef(null);
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    const bar = document.querySelector(`.${styles.progressBar}`);
    if (!bar) return;
    bar.style.width = `${(step / TOTAL_STEPS) * 100}%`;
  }, [step]);

  const validateEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);



  const handleImageChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const filtered = CAMPUS_LIST.filter(c =>
    c.name.toLowerCase().includes(campusSearch.toLowerCase()) ||
    c.short.toLowerCase().includes(campusSearch.toLowerCase()) ||
    c.city.toLowerCase().includes(campusSearch.toLowerCase())
  );
  const publicList  = filtered.filter(c => c.type === 'public');
  const privateList = filtered.filter(c => c.type === 'private');
  const otherList   = filtered.filter(c => c.type === 'other');

  const handleSendOtp = async () => {
    setOtpLoading(true);
    setErrors({});
    try {
      const { data } = await axios.post(`${API_URL}/otp/send`, { email: email.trim() });
      if (data.skipOtp) {
        setOtpVerified(true);
        return true;
      }
      setOtpSent(true);
      setOtpCooldown(60);
      const timer = setInterval(() => {
        setOtpCooldown(c => {
          if (c <= 1) { clearInterval(timer); return 0; }
          return c - 1;
        });
      }, 1000);
      return true;
    } catch (err) {
      setErrors({ email: err.response?.data?.message || "Erreur lors de l'envoi du code" });
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otp.join('');
    if (code.length < 6) { setErrors({ otp: 'Veuillez entrer le code à 6 chiffres' }); return false; }
    setOtpLoading(true);
    setErrors({});
    try {
      await axios.post(`${API_URL}/otp/verify`, { email: email.trim(), code });
      setOtpVerified(true);
      return true;
    } catch (err) {
      setErrors({ otp: err.response?.data?.message || 'Code incorrect' });
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs[index + 1].current.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs[index - 1].current.focus();
    if (e.key === 'Enter') next();
  };

  const next = async () => {
    setErrors({});
    if (step === 1) {
      if (!email.trim())               { setErrors({ email: "L'adresse e-mail est requise" }); return; }
      if (!validateEmail(email.trim())) { setErrors({ email: 'Adresse e-mail invalide' });      return; }
      const res = await handleSendOtp();
      if (res) setStep(2);
      //return;
    }
    if (step === 2) {
      if (otpVerified) { setStep(3); return; }
      const res = await handleVerifyOtp();
      if (res) setStep(3);
      //return;
    }
    if (step === 3) {
      if (!password.trim())            { setErrors({ password: 'Le mot de passe est requis' }); return; }
      if (password.length < 6)         { setErrors({ password: 'Au moins 6 caractères' });      return; }
    }
    if (step === 4) {
      if (!username.trim())            { setErrors({ username: "Le nom d'utilisateur est requis" }); return; }
      if (username.trim().length < 3)  { setErrors({ username: 'Au moins 3 caractères' });           return; }
    }
    setStep(s => s + 1);
  };

  const back = () => { setErrors({}); setStep(s => s - 1); };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!birthday)   { setErrors({ birthday: 'Veuillez sélectionner votre date de naissance' }); return; }
    if (!acceptCgu)  { setErrors({ cgu: 'Vous devez accepter les CGU pour continuer' }); return; }
    setIsLoading(true);
    try {
      let base64Image = null;
      if (imageFile) {
        const reader = new FileReader();
        base64Image = await new Promise((res, rej) => {
          reader.onloadend = () => res(reader.result);
          reader.onerror   = rej;
          reader.readAsDataURL(imageFile);
        });
      }
      const selectedCampus = CAMPUS_LIST.find(c => c.id === campus);
      const { data } = await axios.post(`${API_URL}/signup`, {
        username    : username.trim(),
        userEmail   : email.trim(),
        userPassword: password.trim(),
        userBirthday: birthday,
        userPP      : base64Image,
        followers   : [],
        following   : [],
      });
      if (data?.token) {
        localStorage.setItem('token', data.token);
        navigate('/home', { replace: true });
      } else {
        setErrors({ submit: data?.message || 'Erreur lors de la création du compte' });
      }
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || 'Erreur lors de la création du compte' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKey = e => { if (e.key === 'Enter' && step < TOTAL_STEPS) next(); };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API_URL}/auth/google`, {
        token: credentialResponse.credential
      });
      const data = response?.data;
      if (data && data.token) {
        localStorage.setItem('token', data.token);
        navigate('/home', { replace: true });
      }
    } catch (error) {
      console.error('Erreur Google Auth:', error);
      setErrors({ submit: 'Échec de l\'authentification avec Google' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Brand - Left Side on Desktop */}
        <div className={styles.brand}>
          <div className={styles.logo}>
            <img src={logo} alt="Agora Logo" />
          </div>
          <h1 className={styles.brandName}>agora</h1>
          <p className={styles.brandTagline}>
            Agora vous aide à rester en contact avec votre communauté universitaire.
          </p>
        </div>

        {/* Signup Card - Right Side on Desktop */}
        <div className={styles.formWrapper}>
          {/* Header with Navigation & Progress */}
          <div className={styles.header}>
            {step > 1 && (
              <button className={styles.backButton} onClick={back} type="button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
            )}
            <div className={styles.headerRight}>
              <span className={styles.stepText}>
                Étape {step}/{TOTAL_STEPS}
              </span>
            </div>
          </div>

          <div className={styles.progressBarContainer}>
            <div className={styles.progressBar} />
          </div>

          <div className={styles.formContainer}>
            <form onSubmit={handleSubmit}>
              {/* ── Step 1 : Email ── */}
              {step === 1 && (
                <div className={styles.stepContent}>
                  <h1 className={styles.title}>Votre adresse e-mail</h1>
                  <p className={styles.subtitle}>Entrez l'adresse e-mail que vous utiliserez pour vous connecter</p>
                  <div className={`${styles.inputContainer} ${errors.email ? styles.inputError : ''}`}>
                    <FaEnvelope className={styles.icon} />
                    <input type="email" placeholder="exemple@email.com" value={email}
                      onChange={e => { setEmail(e.target.value); setErrors({}); }}
                      onKeyPress={handleKey} autoFocus />
                  </div>
                  {errors.email && <p className={styles.errorText}>{errors.email}</p>}

                  <div className={styles.divider} style={{ marginTop: '24px' }}>
                    <span>Ou s'inscrire avec</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                    <GoogleLogin
                      onSuccess={handleGoogleLogin}
                      onError={() => setErrors({ submit: 'Échec de connexion avec Google' })}
                      useOneTap
                    />
                  </div>
                </div>
              )}

              {/* ── Step 2 : OTP ── */}
              {step === 2 && (
                <div className={styles.stepContent}>
                  <h1 className={styles.title}>Vérification</h1>
                  <p className={styles.subtitle}>On vient de t'envoyer un code à 6 chiffres sur <strong>{email}</strong>. <br /> <span style={{color: '#f59e0b', fontSize: '0.9em'}}>N'oubliez pas de vérifier vos <strong>spams</strong> !</span></p>
                  
                  <div className={styles.otpRow}>
                    {otp.map((digit, i) => (
                      <input key={i} ref={otpRefs[i]} type="text" maxLength={1} value={digit}
                        className={styles.otpInput} onChange={e => handleOtpChange(i, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(i, e)} autoFocus={i === 0} />
                    ))}
                  </div>

                  {errors.otp && <p className={styles.errorText}>{errors.otp}</p>}

                  <div className={styles.otpActions} style={{marginTop:32, textAlign:'center'}}>
                    <p style={{fontSize:14, color:'var(--text-secondary)', marginBottom:12}}>Tu n'as rien reçu ?</p>
                    <button type="button" className={styles.skipButton} disabled={otpCooldown > 0 || otpLoading} onClick={handleSendOtp} style={{width:'auto', padding:'4px 12px'}}>
                      {otpCooldown > 0 ? `Renvoyer dans ${otpCooldown}s` : 'Renvoyer le code'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* ── Step 3 : Mot de passe ── */}
              {step === 3 && (
                <div className={styles.stepContent}>
                  <h1 className={styles.title}>L'accès</h1>
                  <p className={styles.subtitle}>Choisissez un mot de passe sécurisé (min 6 caractères)</p>
                  <div className={`${styles.inputContainer} ${errors.password ? styles.inputError : ''}`}>
                    <FaLock className={styles.icon} />
                    <input type={showPassword ? 'text' : 'password'} placeholder="Nouveau mot de passe" value={password}
                      onChange={e => { setPassword(e.target.value); setErrors({}); }}
                      onKeyPress={handleKey} autoFocus />
                    <button type="button" className={styles.passwordToggle} onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && <p className={styles.errorText}>{errors.password}</p>}
                </div>
              )}

              {/* ── Step 4 : Username ── */}
              {step === 4 && (
                <div className={styles.stepContent}>
                  <h1 className={styles.title}>Identité numérique</h1>
                  <p className={styles.subtitle}>Choisissez un nom d'utilisateur unique</p>
                  <div className={`${styles.inputContainer} ${errors.username ? styles.inputError : ''}`}>
                    <FaUser className={styles.icon} />
                    <input type="text" placeholder="nom_utilisateur" value={username}
                      onChange={e => { setUsername(e.target.value); setErrors({}); }}
                      onKeyPress={handleKey} autoFocus />
                  </div>
                  {errors.username && <p className={styles.errorText}>{errors.username}</p>}
                </div>
              )}

              {/* ── Step 5 : Photo ── */}
              {step === 5 && (
                <div className={styles.stepContent}>
                  <h1 className={styles.title}>Photo de profil</h1>
                  <p className={styles.subtitle}>Ajoutez une photo pour être reconnu</p>
                  <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" style={{ display: 'none' }} />
                  <div className={styles.imagePickerContainer} onClick={() => fileInputRef.current?.click()}>
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Profile" className={styles.profileImage} />
                        <div className={styles.changePhotoBtn}><FaPen /></div>
                      </>
                    ) : (
                      <div className={styles.imagePlaceholder}>
                        <FaCamera className={styles.imagePlaceholderIcon} />
                        <p>Ajouter</p>
                      </div>
                    )}
                  </div>
                  <button type="button" className={styles.skipButton} onClick={next}>
                    {imagePreview ? 'Changer' : 'Passer cette étape'}
                  </button>
                </div>
              )}

              {/* ── Step 6 : Birthday ── */}
              {step === 6 && (
                <div className={styles.stepContent}>
                  <h1 className={styles.title}>Date de naissance</h1>
                  <p className={styles.subtitle}>Votre date de naissance est requise</p>
                  <div className={styles.inputContainer}>
                    <FaCalendarAlt className={styles.icon} />
                    <input type="date" value={birthday} onChange={e => { setBirthday(e.target.value); setErrors({}); }} max={new Date().toISOString().split('T')[0]} />
                  </div>
                  {errors.birthday && <p className={styles.errorText}>{errors.birthday}</p>}

                  <div className={styles.cguRow}>
                    <input type="checkbox" id="accept-cgu" checked={acceptCgu} onChange={e => { setAcceptCgu(e.target.checked); setErrors({}); }} />
                    <label htmlFor="accept-cgu" className={styles.cguLabel}>
                      J'accepte les <a href="/cgu" className={styles.cguLink}>CGU</a> et la <a href="/politique" className={styles.cguLink}>Politique de Confidentialité</a>
                    </label>
                  </div>
                  {errors.cgu && <p className={styles.errorText}>{errors.cgu}</p>}
                </div>
              )}

              {/* Submit Error Display */}
              {errors.submit && <p className={styles.submitError}>{errors.submit}</p>}

                <button
                  type={step === TOTAL_STEPS ? 'submit' : 'button'}
                  className={`${styles.button} ${isLoading || otpLoading ? styles.buttonDisabled : ''}`}
                  onClick={step === TOTAL_STEPS ? undefined : next}
                  disabled={isLoading || otpLoading}
                >
                  {(isLoading || otpLoading) ? <span className={styles.spinner} /> : (step === TOTAL_STEPS ? 'S\'inscrire' : 'Continuer')}
                </button>

              <div className={styles.footer}>
                <p>Vous avez déjà un compte ? <button type="button" onClick={() => navigate('/')} className={styles.link}>Se connecter</button></p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;